import React from 'react';
import { CheckIcon, CopyIcon, DownloadIcon, PrinterIcon, Trash2Icon, UploadIcon, UsersIcon } from 'lucide-react';
import { Container } from '../../components/ui/Primitives';
import { Button } from '../../components/ui/Button';
import { Field, inputClass } from '../../components/ui/Field';
import { Seo } from '../../components/Seo';
import { Dialog } from '../../components/portal/Dialog';
import { JoinDialog } from '../../components/academy/JoinDialog';
import { Photo } from '../../components/ui/Photo';
import { PHOTOS } from '../../data/photos';
import { useAcademy } from '../../context/Academy';
import { useAcademyHub } from '../../context/AcademyHub';
import { courseProgress } from '../../utils/academyProgress';
import { downloadCsv, parsePeople } from '../../utils/academyFormat';
import type { TeamMember } from '../../types/academyHub';
export function AcademyTeam() {
  const {
    state
  } = useAcademy();
  const {
    hub
  } = useAcademyHub();
  const [joinOpen, setJoinOpen] = React.useState(false);
  if (!state.learner) {
    return <>
        <Seo title="Teams, Cavalla Academy" description="Enrol your team and track their progress." path="/academy/team" noIndex data-id="element-1901" data-mp="sixrjqse5kki" />
        <Container className="grid gap-12 py-16 sm:py-20 lg:grid-cols-2 lg:items-center" data-id="element-1902" data-mp="jjkhox0cutz9">
          <div data-id="element-1903" data-mp="jlx35k5bnmh2">
            <h1 className="font-display text-[32px] leading-tight text-ink sm:text-[40px]" data-id="element-1904" data-mp="nkw64z7ht95v">Train your whole team, free</h1>
            <p className="mt-4 max-w-lg text-[17px] text-body" data-id="element-1905" data-mp="1bbrvzqse1uah">
              Create a team, invite colleagues by email or spreadsheet, assign courses in one step and follow everyone’s progress.
            </p>
            <Button type="button" size="lg" className="mt-8" onClick={() => setJoinOpen(true)} data-id="element-1906" data-mp="12hn91igdl308">
              Create a free account
            </Button>
          </div>
          <Photo photo={PHOTOS.officeAnalysts} aspect="aspect-[16/10]" data-id="element-1907" data-mp="1u92uvaa3ylpe" />
        </Container>
        <JoinDialog open={joinOpen} onClose={() => setJoinOpen(false)} onJoined={() => setJoinOpen(false)} data-id="element-1908" data-mp="1z02s6gn535sf" />
      </>;
  }
  return hub.team ? <TeamDashboard data-id="element-1909" data-mp="28v048gtj7tcw" /> : <CreateTeam data-id="element-1910" data-mp="28vi9g2y69hwy" />;
}
function CreateTeam() {
  const {
    state
  } = useAcademy();
  const {
    createTeam
  } = useAcademyHub();
  const [name, setName] = React.useState('');
  const [org, setOrg] = React.useState(state.learner?.organisation ?? '');
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = 'Please name your team.';
    if (!org.trim()) next.org = 'Please enter the organisation.';
    setErrors(next);
    if (!Object.keys(next).length) createTeam(name, org);
  };
  return <Container className="grid gap-12 py-14 sm:py-20 lg:grid-cols-[1fr_0.9fr]" data-id="element-1911" data-mp="1c6keokqf9jzs">
      <Seo title="Create a team, Cavalla Academy" description="Create a team." path="/academy/team" noIndex data-id="element-1912" data-mp="176y0o92rrzpx" />
      <div data-id="element-1913" data-mp="1284l8b3x9yn4">
        <h1 className="font-display text-[30px] leading-tight text-ink sm:text-[38px]" data-id="element-1914" data-mp="127l4gxq0dgao">Create a team</h1>
        <p className="mt-3 max-w-lg text-[16px] text-body" data-id="element-1915" data-mp="s4pom9hm4ac4">
          You become the team admin. You can invite members, assign courses and export progress reports.
        </p>
        <form onSubmit={submit} noValidate className="mt-8 max-w-lg space-y-5" data-id="element-1916" data-mp="122djczf7xhua">
          <Field id="team-name" label="Team name" required error={errors.name} data-id="element-1917" data-mp="1209cc8m0w2ps">
            <input id="team-name" value={name} onChange={e => setName(e.target.value)} placeholder="Planning and M&E unit" className={inputClass} data-id="element-1918" data-mp="16z48ec903nru" />
          </Field>
          <Field id="team-org" label="Organisation" required error={errors.org} data-id="element-1919" data-mp="edkqjbtw7q3w">
            <input id="team-org" value={org} onChange={e => setOrg(e.target.value)} className={inputClass} data-id="element-1920" data-mp="jbck0aa6rka7" />
          </Field>
          <Button type="submit" size="lg" data-id="element-1921" data-mp="o3vxl78viff0">
            <UsersIcon className="h-4 w-4" aria-hidden="true" data-id="element-1922" data-mp="3msclutdhuypi" />
            Create team
          </Button>
        </form>
      </div>
      <Photo photo={PHOTOS.workshopTable} aspect="aspect-[4/3]" className="hidden lg:block" data-id="element-1923" data-mp="3rqmlpha5ynhg" />
    </Container>;
}
function TeamDashboard() {
  const {
    state
  } = useAcademy();
  const {
    hub,
    removeMember,
    courses
  } = useAcademyHub();
  const team = hub.team!;
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [assignOpen, setAssignOpen] = React.useState(false);
  const [removing, setRemoving] = React.useState<TeamMember | null>(null);
  const assigned = courses.filter(c => team.assigned.includes(c.slug));
  const active = team.members.filter(m => m.status === 'active');
  const invited = team.members.filter(m => m.status === 'invited');
  const percentFor = (member: TeamMember, slug: string): number | null => {
    if (!member.isYou) return null;
    const course = courses.find(c => c.slug === slug);
    return course && state.enrolments[slug] ? courseProgress(course, state).percent : 0;
  };
  const yourAverage = assigned.length ? Math.round(assigned.reduce((sum, c) => sum + (percentFor(team.members[0], c.slug) ?? 0), 0) / assigned.length) : 0;
  const exportCsv = () => {
    const header = ['Name', 'Email', 'Status', ...assigned.map(c => c.title)];
    const rows = team.members.map(m => [m.name, m.email, m.status === 'active' ? 'Active' : 'Invited', ...assigned.map(c => {
      const p = percentFor(m, c.slug);
      return p === null ? 'Not started' : `${p}%`;
    })]);
    downloadCsv(`${team.name.replace(/\W+/g, '-').toLowerCase()}-progress.csv`, [header, ...rows]);
  };
  return <>
      <Seo title={`${team.name}, Cavalla Academy`} description="Team progress." path="/academy/team" noIndex data-id="element-1924" data-mp="2oj8v6vjkv6v7" />
      <Container className="py-12 sm:py-16" data-id="element-1925" data-mp="29m8ero6u6yc1">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between" data-id="element-1926" data-mp="29n9sk0f8lyxg">
          <div data-id="element-1927" data-mp="2eks1vrx62g5z">
            <p className="text-small text-muted" data-id="element-1928" data-mp="2emme8hudgzyg">{team.organisation} · You are the team admin</p>
            <h1 className="mt-1 font-display text-[30px] leading-tight text-ink sm:text-[38px]" data-id="element-1929" data-mp="7rzad6ddhtpf">{team.name}</h1>
          </div>
          <div className="flex flex-wrap gap-3 print:hidden" data-id="element-1930" data-mp="bbq3ih5065hz">
            <Button type="button" variant="secondary" onClick={() => setInviteOpen(true)} data-id="element-1931" data-mp="bdatrukab2a1">
              Invite members
            </Button>
            <Button type="button" onClick={() => setAssignOpen(true)} data-id="element-1932" data-mp="1da8w46roe3q">
              Assign courses
            </Button>
          </div>
        </div>

        <dl className="mt-10 grid grid-cols-2 gap-6 border-y border-line py-6 sm:grid-cols-4" data-id="element-1933" data-mp="3tjfi471f7739">
          <Stat label="Active members" value={active.length} data-id="element-1934" data-mp="u1zae4bqilg0" />
          <Stat label="Pending invites" value={invited.length} data-id="element-1935" data-mp="p34jsg7izslc" />
          <Stat label="Courses assigned" value={assigned.length} data-id="element-1936" data-mp="p5qugoppqo7s" />
          <Stat label="Your completion" value={`${yourAverage}%`} data-id="element-1937" data-mp="kb4r98tsc1ys" />
        </dl>

        <section aria-labelledby="progress" className="mt-12" data-id="element-1938" data-mp="ga18oekcrrtv">
          <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-1939" data-mp="1csqkj83jqfvg">
            <h2 id="progress" className="font-display text-[22px] text-ink" data-id="element-1940" data-mp="1cutjihmofjcb">
              Progress
            </h2>
            <div className="flex gap-4 print:hidden" data-id="element-1941" data-mp="2qp0c1ey0fmt7">
              <button type="button" onClick={exportCsv} className="inline-flex items-center gap-1.5 text-small text-royal hover:text-ink" data-id="element-1942" data-mp="2qql5f18l8zph">
                <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1943" data-mp="2zpoo443ufd3g" />
                Export CSV
              </button>
              <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-1.5 text-small text-royal hover:text-ink" data-id="element-1944" data-mp="34pb2igveqzdu">
                <PrinterIcon className="h-4 w-4" aria-hidden="true" data-id="element-1945" data-mp="39nmd4ttj1dx4" />
                Print or save PDF
              </button>
            </div>
          </div>

          {assigned.length === 0 && <p className="mt-4 rounded-md border border-dashed border-line px-5 py-4 text-[14px] text-body" data-id="element-1946" data-mp="3jeh6ei56xds2">
              No courses assigned yet. Assign one and every member is enrolled straight away.
            </p>}

          <div className="mt-5 overflow-x-auto rounded-md border border-line" data-id="element-1947" data-mp="17t6d6ozzgt9">
            <table className="w-full min-w-[640px] text-left text-[14px]" data-id="element-1948" data-mp="15q51qiea198">
              <thead className="bg-panel text-small text-muted" data-id="element-1949" data-mp="388qi8ndnzfry">
                <tr data-id="element-1950" data-mp="33bzzttee083d">
                  <th scope="col" className="px-4 py-3 font-medium" data-id="element-1951" data-mp="33af8ecndd59q">
                    Member
                  </th>
                  {assigned.map(c => <th key={c.slug} scope="col" className="max-w-[160px] px-4 py-3 font-medium" data-id="element-1952" data-mp="3d8uyqdekoai6">
                      <span className="line-clamp-2" data-id="element-1953" data-mp="2pjad1mr1az57">{c.title}</span>
                    </th>)}
                  <th scope="col" className="w-12 px-4 py-3" data-id="element-1954" data-mp="2klicy5g8k6px">
                    <span className="sr-only" data-id="element-1955" data-mp="2yc3vtu5owgnt">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line" data-id="element-1956" data-mp="2tkvkgsksul80">
                {team.members.map(m => <tr key={m.email} data-id="element-1957" data-mp="25vazk4535dw3">
                    <td className="px-4 py-3" data-id="element-1958" data-mp="25u9r87fgf5i2">
                      <span className="block text-ink" data-id="element-1959" data-mp="21uhcfczgw349">
                        {m.name}
                        {m.isYou && <span className="ml-2 text-small text-muted" data-id="element-1960" data-mp="1rqkh8830voi6">(you)</span>}
                      </span>
                      <span className="block text-small text-muted" data-id="element-1961" data-mp="1whixj7o0gmlf">
                        {m.email}
                        {m.status === 'invited' && <span className="ml-2 text-royal" data-id="element-1962" data-mp="1hto75xxh6ubi">Invite sent</span>}
                      </span>
                    </td>
                    {assigned.map(c => {
                  const p = percentFor(m, c.slug);
                  return <td key={c.slug} className="px-4 py-3" data-id="element-1963" data-mp="2fgeuurv61h3h">
                          {p === null ? <span className="text-muted" data-id="element-1964" data-mp="20je16itezc2d">{m.status === 'invited' ? 'Awaiting join' : 'Not started'}</span> : <span className="flex items-center gap-2" data-id="element-1965" data-mp="25js980v0mm0e">
                              <span className="h-1.5 w-16 overflow-hidden rounded-full bg-charcoal" data-id="element-1966" data-mp="p1w4g3a93cpc">
                                <span className={`block h-full ${p === 100 ? 'bg-ink' : 'bg-gold'}`} style={{
                          width: `${p}%`
                        }} data-id="element-1967" data-mp="u43nfcthzt16" />
                              </span>
                              <span className="text-ink" data-id="element-1968" data-mp="2bkkqvx4tpv9i">{p}%</span>
                            </span>}
                        </td>;
                })}
                    <td className="px-4 py-3 text-right" data-id="element-1969" data-mp="21tq4msrkuvo2">
                      {!m.isYou && <button type="button" onClick={() => setRemoving(m)} aria-label={`Remove ${m.name}`} className="rounded-sm p-1.5 text-muted hover:bg-panel hover:text-ink print:hidden" data-id="element-1970" data-mp="1nugzqp8tnnze">
                          <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-1971" data-mp="1iwf7bdpiacb9" />
                        </button>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-small text-muted" data-id="element-1972" data-mp="11mbbaezudc08">
            Invited colleagues’ progress appears here once they join and the Academy server is connected.
          </p>
        </section>
      </Container>

      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} data-id="element-1973" data-mp="1bbuvbnzajbwo" />
      <AssignDialog open={assignOpen} onClose={() => setAssignOpen(false)} data-id="element-1974" data-mp="1bdxuoymc2he2" />
      <Dialog open={Boolean(removing)} onClose={() => setRemoving(null)} title={`Remove ${removing?.name ?? ''}?`} description="They keep their own account and progress, but lose team-assigned courses and the team badge." data-id="element-1975" data-mp="16k3dc1u7aei5">
        <div className="flex justify-end gap-3" data-id="element-1976" data-mp="1ub8nq1hdgel2">
          <Button type="button" variant="secondary" onClick={() => setRemoving(null)} data-id="element-1977" data-mp="1u8w83uz36gb6">
            Cancel
          </Button>
          <Button type="button" onClick={() => {
          if (removing) removeMember(removing.email);
          setRemoving(null);
        }} data-id="element-1978" data-mp="3w23nt5im2zxl">
            Remove member
          </Button>
        </div>
      </Dialog>
    </>;
}
function Stat({
  label,
  value
}: {
  label: string;
  value: React.ReactNode;
}) {
  return <div data-id="element-1979" data-mp="1zzh0t9sbi46k">
      <dt className="text-small text-muted" data-id="element-1980" data-mp="2011yv9duc596">{label}</dt>
      <dd className="mt-1 font-display text-[28px] leading-none text-ink" data-id="element-1981" data-mp="23zr6smg40ulf">{value}</dd>
    </div>;
}
function InviteDialog({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const {
    state
  } = useAcademy();
  const {
    inviteMembers
  } = useAcademyHub();
  const [text, setText] = React.useState('');
  const [error, setError] = React.useState('');
  const [sent, setSent] = React.useState(0);
  const [copied, setCopied] = React.useState(false);
  const joinLink = `https://academy.cavalladata.com/join?ref=${state.learner?.referralCode ?? ''}&team=1`;
  React.useEffect(() => {
    if (open) {
      setText('');
      setError('');
      setSent(0);
    }
  }, [open]);
  const onFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setText(await file.text());
    event.target.value = '';
  };
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const people = parsePeople(text);
    if (!people.length) {
      setError('Add at least one valid email address, one person per line.');
      return;
    }
    setError('');
    setSent(inviteMembers(people));
  };
  return <Dialog open={open} onClose={onClose} title="Invite members" description="One person per line as name, email. Or upload a CSV with first_name, last_name, email columns." data-id="element-1982" data-mp="3ujiosm68f11h">
      {sent ? <div data-id="element-1983" data-mp="3ksxtp8proenb">
          <p className="flex items-center gap-2 text-[15px] text-ink" data-id="element-1984" data-mp="3ksealoodrk7p">
            <CheckIcon className="h-4 w-4 text-[#4ADE80]" aria-hidden="true" data-id="element-1985" data-mp="3grkkqprdnvc6" />
            {sent} {sent === 1 ? 'person' : 'people'} added to the team.
          </p>
          <p className="mt-3 text-[14px] text-body" data-id="element-1986" data-mp="h5dwhr21p4zk">
            The Academy team has been notified. Invitation emails go out automatically once the Academy server is connected; until then,
            share this link so colleagues can join:
          </p>
          <div className="mt-3 flex overflow-hidden rounded-sm border border-line bg-charcoal" data-id="element-1987" data-mp="1uz6z05w70qg">
            <input readOnly value={joinLink} aria-label="Team join link" className="min-w-0 flex-1 bg-transparent px-3 py-2.5 text-small text-body focus:outline-none" data-id="element-1988" data-mp="3k0kgi4socx9v" />
            <button type="button" onClick={async () => {
          await navigator.clipboard.writeText(joinLink).catch(() => undefined);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 2000);
        }} className="flex items-center gap-1.5 border-l border-line px-3 text-small text-ink hover:bg-panel" data-id="element-1989" data-mp="3o3h4kb0zhll7">
              {copied ? <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1990" data-mp="ykykl90wgpfw" /> : <CopyIcon className="h-4 w-4" aria-hidden="true" data-id="element-1991" data-mp="yjf8luma10jx" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="mt-6 flex justify-end" data-id="element-1992" data-mp="2o1kdlccowpzt">
            <Button type="button" onClick={onClose} data-id="element-1993" data-mp="2o00znseiar17">
              Done
            </Button>
          </div>
        </div> : <form onSubmit={submit} noValidate data-id="element-1994" data-mp="2xrnh46hftmce">
          <Field id="invite-list" label="People to invite" error={error} data-id="element-1995" data-mp="2xqc3ley6x083">
            <textarea id="invite-list" rows={6} value={text} onChange={e => setText(e.target.value)} placeholder={'Amina Diallo, amina@ministry.gov.sn\nKofi Asante, kofi@bank.com.gh'} className={`${inputClass} font-mono text-[13px]`} data-id="element-1996" data-mp="2ss0w3avdb1t1" />
          </Field>
          <label className="mt-3 inline-flex cursor-pointer items-center gap-2 text-small text-royal hover:text-ink" data-id="element-1997" data-mp="2f2gtq65p86k9">
            <UploadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1998" data-mp="2a4fanl89w78s" />
            Upload CSV
            <input type="file" accept=".csv,text/csv" onChange={onFile} className="sr-only" data-id="element-1999" data-mp="3qif88pjnbksv" />
          </label>
          <div className="mt-6 flex justify-end gap-3" data-id="element-2000" data-mp="3skfzvm0pgnuc">
            <Button type="button" variant="secondary" onClick={onClose} data-id="element-2001" data-mp="3sjmscitx9ub5">
              Cancel
            </Button>
            <Button type="submit" data-id="element-2002" data-mp="1ca81uxqt0r0">Add to team</Button>
          </div>
        </form>}
    </Dialog>;
}
function AssignDialog({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const {
    hub,
    assignCourses,
    publishedCourses
  } = useAcademyHub();
  const [picked, setPicked] = React.useState<string[]>([]);
  const already = hub.team?.assigned ?? [];
  React.useEffect(() => {
    if (open) setPicked([]);
  }, [open]);
  return <Dialog open={open} onClose={onClose} title="Assign courses" description="Every team member is enrolled and notified." data-id="element-2003" data-mp="3jse1dta75hmp">
      <ul className="space-y-1.5" data-id="element-2004" data-mp="3ojw2qig4tk87">
        {publishedCourses.map(c => {
        const done = already.includes(c.slug);
        const on = picked.includes(c.slug);
        return <li key={c.slug} data-id="element-2005" data-mp="2w1qea0rpmlfk">
              <label className={`flex items-start gap-3 rounded-sm border px-3.5 py-3 text-[14px] transition-colors duration-150 ${done ? 'cursor-default border-line text-muted' : on ? 'cursor-pointer border-gold bg-charcoal text-ink' : 'cursor-pointer border-line text-body hover:border-gold/50'}`} data-id="element-2006" data-mp="2w0ywbsr1cm7r">
                <input type="checkbox" disabled={done} checked={done || on} onChange={() => setPicked(p => on ? p.filter(s => s !== c.slug) : [...p, c.slug])} className="mt-0.5 accent-[#FF5C1A]" data-id="element-2007" data-mp="z8fikbbiniex" />
                <span data-id="element-2008" data-mp="lhaqpvee3uf5">
                  {c.title}
                  {done && <span className="ml-2 text-small" data-id="element-2009" data-mp="gh4s1v9pd7nk">Assigned</span>}
                </span>
              </label>
            </li>;
      })}
      </ul>
      <div className="mt-6 flex justify-end gap-3" data-id="element-2010" data-mp="bhjy5z9e3php">
        <Button type="button" variant="secondary" onClick={onClose} data-id="element-2011" data-mp="6igp83qagz4g">
          Cancel
        </Button>
        <Button type="button" disabled={!picked.length} onClick={() => {
        assignCourses(picked);
        onClose();
      }} data-id="element-2012" data-mp="1vcmpi4z7icr8">
          Assign {picked.length || ''} {picked.length === 1 ? 'course' : 'courses'}
        </Button>
      </div>
    </Dialog>;
}